<template>
  <v-container fluid class="fill-height pa-0">
    <v-row class="flex-grow-1">
      <v-col cols="12" class="d-flex flex-column">
        <v-card :loading="loading" class="bg-grey-lighten-4 flex-grow-1 d-flex flex-column">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title :key="title" class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex ga-2">
              <v-tooltip v-if="isTaskRoute" location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="cancel">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Fermer</span>
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="saveTask">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                <span>Sauvegarder</span>
              </v-tooltip>

              <v-tooltip v-if="isAddSubtaskEnabled" location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="addSubtask">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Ajouter une tâche</span>
              </v-tooltip>

              <v-tooltip v-if="gotoTaskListBtnEnabled" location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="gotoTaskList">
                    <v-icon>mdi-hammer-wrench</v-icon>
                  </v-btn>
                </template>
                <span>Naviguer vers la Tournée</span>
              </v-tooltip>

              <v-tooltip v-if="isDeleteTaskEnabled" location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="deleteTsk">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>Supprimer</span>
              </v-tooltip>
            </div>
          </v-toolbar>

          <v-card-text>
            <v-container fluid>
              <v-row density="compact">
                <v-col md="12" cols="12">
                  <v-text-field clearable label="Sujet" v-model="localTask.subject" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col md="6" cols="12">
                  <v-text-field v-if="isCodeTourneeFieldDisabled" label="Code Tournée" :model-value="codeTournee"
                    readonly />
                  <v-autocomplete v-else label="Code Tournée" v-model="localTask.taskListId" :items="taskLists"
                    item-title="label" item-value="id" clearable />
                </v-col>
                <v-col md="6" cols="12">
                  <v-autocomplete label="Type d'activité" :items="taskTypes" v-model="localTask.taskType"
                    :readonly="isTaskTypeFieldReadonly" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col :md="isProductFieldDisplayed ? 6 : 12" cols="12">
                  <v-text-field v-if="isLaverieFieldDisabled" label="Laverie" :model-value="localTask.numeroDeContrat"
                    readonly />
                  <v-autocomplete v-else label="Laverie" v-model="localTask.laverieId" :items="laveries"
                    item-title="numeroDeContrat" item-value="id" clearable />
                </v-col>
                <v-col md="6" cols="12">
                  <v-autocomplete v-if="isProductFieldDisplayed" label="Produit" v-model="localTask.productId"
                    :items="productsByLaverie" :item-title="(item) => `${item.aquaNb} (${item.productType})`"
                    item-value="id" clearable :readonly="isProductFieldReadonly" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col md="6" cols="12">
                  <v-autocomplete label="Status" :items="taskStatuses" v-model="localTask.taskStatus" />
                </v-col>
                <v-col md="6" cols="12">
                  <v-autocomplete label="Priorité" :items="taskPriorities" v-model="localTask.priority" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="12">
                  <v-textarea clearable label="Observation" v-model="localTask.observation" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="12">
                  <SubtaskList :subtasks="localTask.subtasks" @remove="removeSubtask" @update="updateSubtask" />
                </v-col>
              </v-row>

              <v-row v-if="isPanneSectionEnabled" density="compact">
                <v-col cols="12">
                  <v-card variant="outlined">
                    <v-card-title>Panne</v-card-title>
                    <v-divider />
                    <v-card-text>
                      <div v-if="!panne" class="text-center text-medium-emphasis">
                        Aucune panne trouvé
                      </div>
                      <div v-else class="d-flex flex-column ga-4">
                        <div>
                          <div class="text-subtitle-1 font-weight-medium">
                            {{ panne.name }}
                          </div>
                          <div v-if="panne.desc" class="text-body-2 text-medium-emphasis">
                            {{ panne.desc }}
                          </div>
                          <div class="mt-2 text-body-2">
                            <div><strong>Date de création:</strong> {{ formatDate(panne.creationDate) }}</div>
                            <div><strong>Réparée:</strong> {{ panne.repaired ? "Oui" : "Non" }}</div>
                            <div v-if="panne.repaired"><strong>Date de réparation:</strong> {{ formatDate(panne.reparationDate) }}</div>
                          </div>
                        </div>
                        <div>
                          <div class="text-subtitle-2 font-weight-medium mb-1">Pièces installées</div>
                          <div
                            v-if="!panne.installedSpareParts || panne.installedSpareParts.length === 0"
                            class="text-body-2 text-medium-emphasis"
                          >
                            Aucune pièce installée
                          </div>
                          <v-list v-else density="compact" class="bg-transparent pa-0">
                            <v-list-item
                              v-for="sparePart in panne.installedSpareParts"
                              :key="sparePart.id"
                              class="px-0"
                            >
                              <v-list-item-title>
                                {{ sparePart.displayName || sparePart.sparepartId }}
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                Quantite: {{ sparePart.quantity }}
                              </v-list-item-subtitle>
                            </v-list-item>
                          </v-list>
                        </div>
                        <div v-if="localTask.productId" class="d-flex ga-2 justify-end">
                          <v-btn
                            color="primary"
                            variant="text"
                            @click="goToPanne(localTask.productId, panne.id)"
                          >
                            Ouvrir la panne
                          </v-btn>
                          <v-btn
                            color="primary"
                            variant="text"
                            @click="goToProduct(localTask.productId)"
                          >
                            Ouvrir le produit
                          </v-btn>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog :scrim="true" v-model="addSubtaskModal" max-width="600" persistent>
      <v-card>
        <v-card-title>{{ modalTitle }}</v-card-title>
        <v-card-text>{{ modalText }}</v-card-text>
        <v-card-text>
          <v-textarea clearable label="Description" v-model="subtask.name" counter="255" />
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="saveSubtask">Confirmer</v-btn>
          <v-btn color="red" @click="cancelAddSubtask">Annuler</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmDialog v-model="deleteSubtaskDialog" dialog-title="Suppression"
      dialog-text="Voulez vous supprimer cette tâche?" @confirm="onConfirmSubtaskRemoval"
      @cancel="onCancelSubtaskRemoval" confirm-button-text="Confirmer" cancel-button-text="Annuler" />

    <ConfirmDialog v-model="deleteTaskDialog" dialog-title="Suppression"
      dialog-text="Voulez vous supprimer cette activité?" @confirm="onConfirmTaskDeletion"
      @cancel="onCancelTaskDeletion" confirm-button-text="Confirmer" cancel-button-text="Annuler" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, toRaw } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { Priority, Task } from "@/types";
import { Subtask } from "@/types";
import { TaskList } from "@/types";
import { Laverie } from "@/types";
import { Product } from "@/types";
import { Panne } from "@/types";
import { TaskType, TaskStatus, TaskListStatus } from "@/types";
import { useAppStore } from "@/store/app";
import { getTaskById, postTask, putTask, deleteTask } from "@/utils/task-api";
import { getAllLaveries } from "@/utils/laverie-api";
import { getTaskListById, getFilteredTaskLists } from "@/utils/tasklist-api";
import { getProductsByLaverieId } from "@/utils/product-api";
import { fetchPanneByTask } from "@/utils/panne-api";
import SubtaskList from "@/components/SubtaskList.vue";

const props = withDefaults(defineProps<{
  task?: Task | null;
  cardClass?: string;
}>(), {
  task: null,
  cardClass: "",
});

const emit = defineEmits([
  "update:task",
  "delete:task",
  "status-changed",
  "name-changed",
]);

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const modalTitle = "Ajout";
const modalText = "Ajouter une tâche à cette activité";

const localTask = ref<Task>(createEmptyTask());
const selectedSubtask = ref<Subtask | null>(null);

const loading = ref(false);
const deleteSubtaskDialog = ref(false);
const deleteTaskDialog = ref(false);
const addSubtaskModal = ref(false);

const title = computed(() => isCreatingNewTask.value ? "Nouvelle activité" : "Editer l'activité");
const subtask = ref<Subtask>(createEmptySubtask());

const taskLists = ref<TaskList[]>([]);
const laveries = ref<Laverie[]>([]);
const productsByLaverie = ref<Product[]>([]);
const panne = ref<Panne | null>(null);
const taskTaskList = ref<TaskList | null>(null);

const taskTypes = computed(() => {
  if (isCreatingNewTask.value) {
    return Object.values(TaskType);
  }

  if (localTask.value.taskType === TaskType.PREPARATION) {
    return [TaskType.PREPARATION, TaskType.INSTALLATION];
  }

  return localTask.value.taskType ? [localTask.value.taskType] : [];
});
const taskStatuses = Object.values(TaskStatus);
const taskPriorities = Object.values(Priority);

const isTaskRoute = computed(() => {
  const name = String(route.name);
  return name === "task" || name === "newtask";
});

const isCreatingNewTask = computed(() => route.name === "newtask" || !localTask.value?.id);
const isTaskTypeFieldReadonly = computed(() => {
  if (isCreatingNewTask.value) {
    return false;
  }

  return localTask.value.taskType !== TaskType.PREPARATION;
});
const isAddSubtaskEnabled = computed(() => !!localTask.value?.id);
const isDeleteTaskEnabled = computed(() => !!props.task);
const gotoTaskListBtnEnabled = computed(
  () => isTaskRoute.value && localTask.value.taskListId
);

const isLaverieFieldDisabled = computed(() => !!localTask.value && !!localTask.value.id);
const isProductFieldDisplayed = computed(() => localTask.value.taskType === TaskType.PANNE);
const isProductFieldReadonly = computed(() => {
  if (isCreatingNewTask.value) {
    return false;
  }

  if (
    localTask.value.taskType === TaskType.PANNE &&
    (localTask.value.productId === null || localTask.value.productId === undefined)
  ) {
    return false;
  }

  return true;
});
const isCodeTourneeFieldDisabled = computed(() => !!props.task);
const codeTournee = computed(() => taskTaskList.value?.codeTournee);
const isPanneSectionEnabled = computed(() => 
  localTask.value.taskType === TaskType.PANNE
);

// Clone the task locally if it's passed as a prop
watch(
  () => props.task,
  () => cloneTask(),
  { immediate: true }
);

watch(
  () => localTask.value.taskStatus,
  (s, old) => {
    if (s !== old) emit("status-changed", s);
  }
);

watch(
  () => localTask.value.laverieId,
  (id, oldId) => {
    if (id && id !== oldId) {
      initializeProductsByLaverie(id);
      onLaverieChanged(id);
    } else if (!id) {
      productsByLaverie.value = [];
    }
  },
  { immediate: true }
);

watch(
  () => ({
    taskId: localTask.value.id,
    taskType: localTask.value.taskType,
    taskStatus: localTask.value.taskStatus,
    laverieId: localTask.value.laverieId,
    productId: localTask.value.productId,
    productsLength: productsByLaverie.value.length
  }),
  async (newVal) => {
    if (isPanneSectionEnabled.value && newVal.taskId) {
      await initializePanne();
    } else {
      panne.value = null;
    }
  },
  { deep: true, immediate: true }
);

async function initializeProductsByLaverie(laverieId: string) {
  productsByLaverie.value = await getProductsByLaverieId(laverieId);

  // Ensure pannes are fetched after the full products list is available.
  if (isPanneSectionEnabled.value) {
    await initializePanne();
  }
}

async function initializePanne() {
  if (!localTask.value.id || localTask.value.taskType !== TaskType.PANNE) {
    panne.value = null;
    return;
  }

  panne.value = await fetchPanneByTask(localTask.value.id);
}

function createEmptyTask(): Task {
  return {
    id: undefined,
    name: "",
    subject: "",
    laverieId: undefined,
    taskListId: undefined,
    taskType: undefined,
    taskStatus: undefined,
    priority: undefined,
    observation: "",
    subtasks: [],
  } as Task;
}

function createEmptySubtask(): Subtask {
  return {
    id: undefined,
    name: "",
  } as Subtask;
}

function onCancelSubtaskRemoval() {
  deleteSubtaskDialog.value = false;
  selectedSubtask.value = null;
}

function cloneTask() {
  if (props.task) {
    const raw = toRaw(props.task);
    localTask.value = structuredClone(raw);
    localTask.value.subtasks ||= [];
  } else {
    localTask.value = createEmptyTask();
  }
}

function addSubtask() {
  addSubtaskModal.value = true;
}

async function saveSubtask() {
  addSubtaskModal.value = false;
  localTask.value.subtasks.push(subtask.value);
  subtask.value = {} as Subtask;
  await saveTask();
  if (localTask.value.id) {
    const fresh = await getTaskById(localTask.value.id);
    localTask.value = fresh;
  }
}

function removeSubtask(item: Subtask) {
  deleteSubtaskDialog.value = true;
  selectedSubtask.value = item;
}

function updateSubtask(updated: Subtask) {
  const index = localTask.value.subtasks.findIndex(
    (s) => s === updated || s.id === updated.id
  );
  if (index !== -1) {
    localTask.value.subtasks[index] = updated;
    saveTask();
  }
}

function onConfirmSubtaskRemoval() {
  deleteSubtaskDialog.value = false;

  if (!selectedSubtask.value?.id) {
    return;
  }

  localTask.value.subtasks = localTask.value.subtasks.filter(
    (s) => s.id !== selectedSubtask.value!.id
  );

  saveTask();
}

function cancelAddSubtask() {
  addSubtaskModal.value = false;
}

async function saveTask() {
  if (!localTask) {
    return;
  }
  try {
    loading.value = true;

    const savedTask = await doSaveTask(localTask.value);

    if (savedTask) {
      // Update local copy with server response
      localTask.value = savedTask;

      if (localTask.value.taskType === TaskType.PANNE && localTask.value.id) {
        await initializePanne();
      }

      // Notify parent
      emit("update:task", savedTask);
    }
  } finally {
    loading.value = false;
  }
}

async function doSaveTask(task: Task): Promise<Task> {
  if (!task.id) {
    const newTask = await postTask(task);

    if (isCreatingNewTask.value) {
      router.replace({
        name: "task",
        params: { id: newTask.id },
      });
    }

    appStore.sendSuccessNotice("Une nouvelle activité a été ajoutée");
    return newTask;
  } else {
    await putTask(task);
    appStore.sendSuccessNotice("L'activité a été mise à jour");
    return task;
  }
}


function deleteTsk() {
  deleteTaskDialog.value = true;
}

async function onConfirmTaskDeletion() {
  deleteTaskDialog.value = false;
  if (props.task?.id) {
    await deleteTask([props.task.id as string]);
  }
  emit("delete:task", props.task);
}

function onCancelTaskDeletion() {
  deleteTaskDialog.value = false;
}

function cancel() {
  router.push({ name: "tasks" });
}

function gotoTaskList() {
  router.push({
    name: "tasklist",
    params: { id: localTask.value.taskListId },
  });
}

function goToProduct(productId?: string) {
  if (!productId) {
    return;
  }

  router.push({
    name: "product",
    params: { id: productId },
  });
}

function goToPanne(productId?: string, panneId?: string) {
  if (!productId || !panneId) {
    return;
  }

  router.push({
    name: "panne",
    params: { productId, id: panneId },
  });
}

function formatDate(date?: string) {
  if (!date) {
    return "-";
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString("fr-FR");
}

async function onLaverieChanged(id: string) {
  if (localTask.value.id) return;
  const lav = laveries.value.find((l) => l.id === id);
  if (lav) emit("name-changed", lav.name);
}

onMounted(async () => {
  // If the task is not passed as a prop, try to load by route id
  // only if this form is not displayed as a child of TaskListEditor
  if (!props.task && isTaskRoute.value && route.params.id) {
    localTask.value = await getTaskById(route.params.id as string);
  }

  // Load for the autocomplete fields only if field are enabled
  const promises: Promise<any>[] = [];
  promises.push(
    getAllLaveries().then((lav) => (laveries.value = lav))
  );
  promises.push(
    getFilteredTaskLists([
      TaskListStatus.A_FAIRE,
      TaskListStatus.EN_COURS,
    ]).then((lists) => (taskLists.value = lists))
  );


  await Promise.all(promises);

  // taskTaskList is needed in the textfield when the autocomplete taskList field is disabled
  // or when the task is open standalone and the taskList is approuved
  if (localTask.value && localTask.value.taskListId) {
    taskTaskList.value = await getTaskListById(localTask.value.taskListId);
    // Add taskTaskList to the array if it's approuved to be able to see it the autocomplete
    if (
      taskTaskList.value.taskListStatus === TaskListStatus.APPROUVE &&
      !taskLists.value.includes(taskTaskList.value)
    ) {
      taskLists.value.push(taskTaskList.value);
    }
  }

});
</script>
<style scoped>
.task-form-root {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
}
</style>
